import React, { useCallback, useContext, useEffect, useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import {
  Alert,
  Button,
  FormControlLabel,
  Grid,
  LinearProgress,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import mainStyles from "styles";

import { GetFeatureSettingsResponse } from "@safetyculture/s12-apis-connect-web/s12/inductions/admin/v1/inductions_admin_service_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { GetTrainingAppFeaturesResponse } from "@safetyculture/s12-apis-connect-web/s12/training/admin/v1/appfeatures_pb";

import {
  getInductionsFeatureSettings,
  updateInductionsFeatureSettings,
} from "api/endpoints/inductions";
import { getAppFeatures, updateAppFeatures } from "api/endpoints/training";
import { callOptionsHeaders } from "api/helpers";
import EmptyTableResults from "components/shared/table/empty-table-results";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import { PermissionsContext } from "context/permissions-context";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

interface OrgTrainingAppFeaturesTabProps {
  roleId: string;
  regionCode: string;
}

enum TrainingAppFeature {
  LEADERBOARD = "LeaderboardEnabled",
  SCORM_EXPORT = "ScormExportEnabled",
  COURSE_COLLECTIONS = "CourseCollectionsEnabled",
  TEAM_VIEW = "TeamViewEnabled",
  PRACTICAL_ASSESSMENT_MANAGER_RESTRICTION = "PracticalAssessmentManagerRestrictionEnabled",
  CONTRACTOR_ONBOARDING_BUNDLE = "ContractorOnboardingBundleEnabled",
}

const OrgTrainingAppFeaturesTab = ({
  roleId,
  regionCode,
}: OrgTrainingAppFeaturesTabProps) => {
  const classes = mainStyles();
  const { showError } = useContext(SnackbarContext);
  const [orgAppFeatures, setOrgAppFeatures] = useState<
    GetTrainingAppFeaturesResponse | undefined
  >(undefined);
  const [inductionsFeatureSettings, setInductionsFeatureSettings] = useState<
    GetFeatureSettingsResponse | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  const { hasPermissions, permissions } = useContext(PermissionsContext);

  const canUpdateTrainingAppFeatures =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_ORG_TRAINING_APP_FEATURES]);

  const checkPermissionsAndShowError = (): boolean => {
    if (!canUpdateTrainingAppFeatures) {
      showError(
        new Error(
          "You don't have permission to modify training features",
        ) as GrpcError,
      );
      return false;
    }
    return true;
  };

  const handleFeatureUpdate = (key: string, newValue: boolean) => {
    if (!checkPermissionsAndShowError()) return;

    setOrgAppFeatures((prev) => {
      if (!prev) return prev;
      const updatedState = prev.clone();
      updatedState.features[key] = newValue;
      return updatedState;
    });
  };

  const handleLeaderboardUpdate = (newValue: boolean) => {
    handleFeatureUpdate(TrainingAppFeature.LEADERBOARD, newValue);
  };

  const handleScormExportUpdate = (newValue: boolean) => {
    handleFeatureUpdate(TrainingAppFeature.SCORM_EXPORT, newValue);
  };

  const handleCourseCollectionsUpdate = (newValue: boolean) => {
    handleFeatureUpdate(TrainingAppFeature.COURSE_COLLECTIONS, newValue);
  };

  const handleTeamViewUpdate = (newValue: boolean) => {
    handleFeatureUpdate(TrainingAppFeature.TEAM_VIEW, newValue);
  };

  const handlePracticalAssessmentManagerRestrictionUpdate = (
    newValue: boolean,
  ) => {
    handleFeatureUpdate(
      TrainingAppFeature.PRACTICAL_ASSESSMENT_MANAGER_RESTRICTION,
      newValue,
    );
  };

  const handleContractorOnboardingBundleUpdate = (newValue: boolean) => {
    if (!checkPermissionsAndShowError()) return;

    setInductionsFeatureSettings((prev) => {
      if (!prev) return prev;
      const updatedState = prev.clone();
      if (updatedState.adminFeatureSettings) {
        updatedState.adminFeatureSettings.contractorBundleEnabled = newValue;
      }
      return updatedState;
    });
  };

  const handleSave = async () => {
    if (!checkPermissionsAndShowError()) return;

    if (
      !orgAppFeatures ||
      !inductionsFeatureSettings ||
      isSaving ||
      isLoading
    ) {
      showError(new Error("No features data available.") as GrpcError);
      return;
    }

    setIsSaving(true);

    try {
      await Promise.all([
        updateAppFeatures(
          {
            organisationID: roleId,
            features: orgAppFeatures.features,
          },
          callOptionsHeaders(regionCode, roleId),
        ),
        updateInductionsFeatureSettings(
          {
            organisationID: roleId,
            contractorBundleEnabled:
              inductionsFeatureSettings.adminFeatureSettings
                ?.contractorBundleEnabled ?? false,
          },
          callOptionsHeaders(regionCode, roleId),
        ),
      ]);
      setShowSuccessSnackbar(true);
    } catch (error) {
      showError(error as GrpcError);
      // Refresh the feature list and inductions feature settings to get data from BE further indicating to user their change didn't go through
      featureList();
      getInductionsFeatureSettings(
        {
          organisationID: roleId,
        },
        callOptionsHeaders(regionCode, roleId),
      ).then((inductionsRes) => {
        setInductionsFeatureSettings(inductionsRes);
      });
    } finally {
      setIsSaving(false);
    }
  };

  const displayFeatureRow = (
    displayLabel: string,
    featureKey: string,
    value: boolean,
    id: string,
    onChange: (newValue: boolean) => void,
  ) => {
    return (
      <TableRow key={featureKey}>
        <TableCell>
          <Grid item xs container justifyContent="left">
            <FormControlLabel
              control={
                <Switch
                  checked={value}
                  name={featureKey}
                  color="primary"
                  data-testid={`training-app-feature-switch-${id}`}
                  disabled={
                    isLoading || isSaving || !canUpdateTrainingAppFeatures
                  }
                  onChange={(_, checked) => onChange(checked)}
                />
              }
              label={displayLabel}
              labelPlacement="start"
            />
          </Grid>
        </TableCell>
      </TableRow>
    );
  };

  const featureList = useCallback(() => {
    setIsLoading(true);
    Promise.all([
      getAppFeatures(
        {
          organisationID: roleId,
        },
        callOptionsHeaders(regionCode, roleId),
      ),
      getInductionsFeatureSettings(
        {
          organisationID: roleId,
        },
        callOptionsHeaders(regionCode, roleId),
      ),
    ])
      .then(([appFeaturesRes, inductionsRes]) => {
        setOrgAppFeatures(appFeaturesRes);
        setInductionsFeatureSettings(inductionsRes);
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  }, [roleId, regionCode, showError]);

  useEffect(() => {
    featureList();
  }, [featureList]);

  return (
    <>
      <Grid
        container
        className={`${classes.spaceRight} ${classes.featureTabContainer}`}
      >
        <Grid item xs={12}>
          <TableContainer component={Paper} sx={classes.featureTableContainer}>
            <Table aria-label="org-TrainingAppFeatures-table">
              <TableHead></TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && !orgAppFeatures && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <EmptyTableResults
                        tableHeading={"Training App Features"}
                      />
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && orgAppFeatures && (
                  <>
                    {displayFeatureRow(
                      "Enable Leaderboard:",
                      TrainingAppFeature.LEADERBOARD,
                      orgAppFeatures.features[TrainingAppFeature.LEADERBOARD] ??
                        false,
                      "leaderboard",
                      handleLeaderboardUpdate,
                    )}
                    {displayFeatureRow(
                      "Enable Scorm Export:",
                      TrainingAppFeature.SCORM_EXPORT,
                      orgAppFeatures.features[
                        TrainingAppFeature.SCORM_EXPORT
                      ] ?? false,
                      "scormexport",
                      handleScormExportUpdate,
                    )}
                    {displayFeatureRow(
                      "Enable Course Collections:",
                      TrainingAppFeature.COURSE_COLLECTIONS,
                      orgAppFeatures.features[
                        TrainingAppFeature.COURSE_COLLECTIONS
                      ] ?? false,
                      "coursecollections",
                      handleCourseCollectionsUpdate,
                    )}
                    {displayFeatureRow(
                      "Enable Team View:",
                      TrainingAppFeature.TEAM_VIEW,
                      orgAppFeatures.features[TrainingAppFeature.TEAM_VIEW] ??
                        false,
                      "teamview",
                      handleTeamViewUpdate,
                    )}
                    {displayFeatureRow(
                      "Enable PA Manager Restriction:",
                      TrainingAppFeature.PRACTICAL_ASSESSMENT_MANAGER_RESTRICTION,
                      orgAppFeatures.features[
                        TrainingAppFeature
                          .PRACTICAL_ASSESSMENT_MANAGER_RESTRICTION
                      ] ?? false,
                      "practicalassessmentmanagerrestriction",
                      handlePracticalAssessmentManagerRestrictionUpdate,
                    )}
                    {displayFeatureRow(
                      "Enable Contractor Onboarding Bundle:",
                      TrainingAppFeature.CONTRACTOR_ONBOARDING_BUNDLE,
                      inductionsFeatureSettings?.adminFeatureSettings
                        ?.contractorBundleEnabled ?? false,
                      "contractoronboardingbundle",
                      handleContractorOnboardingBundleUpdate,
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid
          item
          xs={12}
          container
          justifyContent="flex-end"
          sx={classes.saveButtonContainer}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isLoading || isSaving}
            data-testid="save-features-button"
            sx={{
              ...classes.saveButton,
              display: canUpdateTrainingAppFeatures ? "inline-flex" : "none",
            }}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={showSuccessSnackbar}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => {
          setShowSuccessSnackbar(false);
        }}
        data-testid="org-app-features-success-toast"
      >
        <Alert
          icon={<CheckIcon fontSize="inherit" />}
          severity="success"
          variant="filled"
        >
          Org App Features Successfully Updated
        </Alert>
      </Snackbar>
    </>
  );
};

export default OrgTrainingAppFeaturesTab;
